import React, { Component } from 'react'
import { connect } from 'react-redux'
import { Trans, withI18n, withI18nProps } from '@lingui/react'
import compose from 'lodash/fp/compose'

import urlFor, { QueryParams } from 'shared/tools/url-helper'

import { getNonExpiredAccessLevels } from 'client/bookmate/selectors/subscription-selectors'

import { getImpressionsByIds } from 'client/bookmate/reducers/impressions-reducer'
import { prepareDefinitionListData } from 'client/shared/helpers/definition-list-helpers'
import errorBoundary from 'client/shared/decorators/error-boundary'
import { getAllEmotions } from 'client/bookmate/reducers/emotions-reducer'

import withMarketing, {
  MarketingProps,
} from 'client/shared/decorators/with-marketing'

import {
  getBookById,
  getBooksByIds,
} from 'client/bookmate/selectors/book-selector'

import { getSecondaryAuthors } from 'client/bookmate/helpers/author-helpers'
import { hasAppropriateActiveSubscription } from 'client/shared/helpers/access-helpers'

import Spacer from 'client/shared/blocks/spacer'
import Linka from 'client/shared/blocks/linka'
import ExpandableText from 'client/shared/blocks/expandable-text'
import Topic from 'client/bookmate/blocks/topic'
import TopicList from 'client/bookmate/blocks/topic-list'
import List from 'client/bookmate/blocks/list'

import SerialEpisode from 'client/bookmate/featured/serial-page/serial-episode'
import RelatedOrSeriesEpisodes from 'client/bookmate/blocks/related-or-series-episodes'
import DefinitionList from 'client/shared/blocks/definition-list'
import ExternalLink from 'client/bookmate/blocks/external-link'

import { Heading } from 'client/shared/blocks/heading'
import ImpressionsIndex from 'client/bookmate/blocks/impressions-index'
import BookShelvesIndexBox from 'client/bookmate/boxes/book-shelves-index-box'
import BookQuotesIndexBox from 'client/bookmate/boxes/book-quotes-index-box'

import { SerialProps } from 'client/shared/types/serial'
import { SeriesProps } from 'client/shared/types/series'
import { BookProps } from 'client/shared/types/book'
import { ImpressionProps } from 'client/shared/types/impression'
import { EmotionRatingProps, EmotionProps } from 'client/shared/types/emotion'
import { BookLink } from 'client/shared/blocks/book-link/book-link'
import { isEmpty } from 'lodash'
import { State as ConnectedState, Dispatch } from 'shared/types/redux'

import { SERIAL } from 'client/bookmate/reducers/serial-reducer'

import { sendOnClickAnalytics } from 'shared/tools/analytics-helper'

type Props = {
  serial: SerialProps
  impressionsIndex: ImpressionProps[]
  fromSeries?: string
  userDevice: string
  emotionRating: EmotionRatingProps[]
  emotions: EmotionProps[]
  hasCurrentUserImpression: boolean
  episodes: BookProps[]
  locale: string
  domain: string
  audioSeries: SeriesProps[]
  dispatch: Dispatch
  userId: number
  query?: QueryParams
  userActiveAccessLevels: string[]
  publisherResources: SerialProps[]
} & withI18nProps &
  MarketingProps

class SerialIndexPage extends Component<Props> {
  getSecondaryData() {
    const {
      i18n,
      query,
      serial: { translators = [] },
    } = this.props

    return [
      {
        resource: translators,
        label: i18n._('book.translator', { count: translators.length }),
        value: getSecondaryAuthors(translators, query),
      },
    ]
  }

  renderSecondaryData(): boolean | JSX.Element {
    const data = prepareDefinitionListData(this.getSecondaryData())

    return data.length > 0 && <DefinitionList data={data} />
  }

  renderAboutSerialBody(): JSX.Element {
    const {
      userDevice,
      publisherResources,
      serial,
      serial: { annotation, external_links, topics, uuid, language },
      episodes,
      fromSeries,
      query,
    } = this.props

    const isMobileSize = userDevice === 'mobile'

    return (
      <>
        {!isMobileSize && <Spacer size={8} />}
        <ExpandableText
          bookLang={language}
          text={annotation}
          maxHeight={isMobileSize ? 72 : 144}
        />
        <Spacer size={24} />
        {topics && topics.length > 0 && (
          <TopicList>
            {topics.map(topic => (
              <Topic
                {...topic}
                resource="books"
                key={topic.uuid}
                query={query}
              />
            ))}
          </TopicList>
        )}
        <Spacer size={24} />
        {serial && this.renderSecondaryData()}
        <Spacer size={24} />
        {this.renderAudioSerialLink()}
        <Spacer size={40} />
        {episodes.length > 0 && this.renderEpisodes()}
        <RelatedOrSeriesEpisodes
          headerRank={2}
          seriesList={serial.series_list}
          fromSeries={fromSeries}
          resourceType="serial"
          publisherResources={publisherResources}
          uuid={uuid}
        />
        {external_links && external_links.length > 0 && (
          <List
            headerRank={2}
            kind="index"
            type="slider"
            header="book.serial_media_links_title"
          >
            {external_links.map(link => (
              <ExternalLink
                externalLink={link}
                kind={external_links.length > 1 ? 'grid' : 'list'}
              />
            ))}
          </List>
        )}
      </>
    )
  }

  renderEpisodes = (): JSX.Element => {
    const {
      episodes,
      serial: { library_card, uuid, can_be_read },
      locale,
      userActiveAccessLevels,
      dispatch,
      userId,
      query,
    } = this.props

    const sortByPosition = (a, b) => a.position - b.position

    const sort = (min, max) =>
      library_card && library_card.progress > 0
        ? sortByPosition(max, min)
        : sortByPosition(min, max)

    return (
      <div className="short-episodes-list">
        <Heading rank={2} kind="default">
          <Trans id="book.serial_episodes_title" />
        </Heading>
        <Spacer size={16} />
        {episodes
          .sort(sort)
          .slice(0, 5)
          .map((episode, index) => (
            <SerialEpisode
              query={query}
              onClick={() =>
                sendOnClickAnalytics({
                  dispatch,
                  resourceType: SERIAL,
                  itemUuid: episode.uuid,
                  screenName: `${SERIAL}_page`,
                  position: index + 1,
                  userId,
                  resourceUuid: uuid,
                })
              }
              key={episode.uuid}
              episode={episode}
              locale={locale}
              canSerialBeRead={can_be_read}
              hasAccess={hasAppropriateActiveSubscription(
                episode,
                userActiveAccessLevels,
              )}
            />
          ))}
        {episodes.length > 5 && (
          <Linka path={urlFor.serialEpisodes(uuid, query)}>
            <div className="short-episodes-list__show-button">
              <Trans id="book.show_episodes_button" />
            </div>
          </Linka>
        )}
        <Spacer size={64} />
      </div>
    )
  }

  renderAudioSerialLink(): JSX.Element[] | null {
    const { audioSeries, app } = this.props

    if (isEmpty(audioSeries)) {
      return null
    }

    return audioSeries.map(series => (
      <BookLink kind="series" query={app.storedQuery} item={series} />
    ))
  }

  render(): JSX.Element {
    const {
      serial,
      impressionsIndex,
      emotionRating,
      emotions,
      query,
      hasCurrentUserImpression,
    } = this.props

    return (
      <>
        {this.renderAboutSerialBody()}
        <Spacer size={64} />
        <ImpressionsIndex
          headerRank={2}
          resource={serial}
          query={query}
          impressionsIndex={impressionsIndex}
          emotionRating={emotionRating}
          emotions={emotions}
          hasCurrentUserImpression={hasCurrentUserImpression}
        />
        <BookQuotesIndexBox headerRank={2} resourceType="serial" />
        <BookShelvesIndexBox headerRank={2} resourceType="serial" />
        <Spacer size={24} />
      </>
    )
  }
}

const connectWrapper = connect((state: ConnectedState) => ({
  userDevice: state.app.size,
  serial: getBookById(state, state.serial.uuid),
  episodes: getBooksByIds(state, state.serial.episodes.all),
  userActiveAccessLevels: getNonExpiredAccessLevels(state),
  impressionsIndex: getImpressionsByIds(state, state.serial.impressionsIndex),
  emotionRating: state.serial.emotionRating,
  publisherResources: state.serial.publisherResources,
  emotions: getAllEmotions(state),
  hasCurrentUserImpression: Boolean(state.serial.currentUserImpressionUuid),
  domain: state.app.domain,
  query: state.app.storedQuery,
  userId: state.currentUser.data.id,
  audioSeries: state.serial.series.filter((series: SeriesProps) =>
    series.kinds.includes('audio'),
  ),
}))

const wrappers = compose(
  connectWrapper,
  errorBoundary,
  withMarketing,
  withI18n({ update: true }),
)

export default wrappers(SerialIndexPage)
